
import Seo from '../../components/Seo';
export default function OperationsPage() {
  
  const chk = (
    <svg aria-hidden="true" className="chk" viewBox="0 0 16 16"><path fill="none" stroke="#007A6E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg>
  );
  const dots = (
    <span className="dots"><span style={{background: '#ff5f57'}}></span><span style={{background: '#febc2e'}}></span><span style={{background: '#28c840'}}></span></span>
  );
  return (
    <>
      <Seo title="Operations Workflow Optimization & Productivity Software | TeamGrid™" description="Find the queue that is slowing your operation while there is still time to move people. Bottlenecks, idle versus active time and live SLA breach risk." />
<header className="sect" style={{paddingBottom: '24px'}}>
  <div className="container">
    <div className="frow" style={{padding: '0'}}>
      <div>
        <span className="hero-badge"><img className="mark" src="/assets/mark-teamgrid.svg" alt="" />Solutions › Operations &amp; Execution <span className="arr">›</span></span>
        <h1 style={{fontSize: '52px', lineHeight: '57px', letterSpacing: '-2.1px', fontWeight: '700', color: 'var(--ink-hero)', marginBottom: '22px'}}>Find the bottleneck<br /><span style={{color: '#a9aab3'}}>before the SLA does</span></h1>
        <p className="fd" style={{fontSize: '17px', lineHeight: '26px', color: 'var(--ink-soft)', marginBottom: '26px', maxWidth: '520px'}}>Ops work fails quietly. A queue backs up on Tuesday, nobody says anything, and the miss shows up in Friday&apos;s SLA report. TeamGrid shows where work slows down while there is still time to move people.</p>
        <ul className="hero-checks">
          <li>{chk}<span><b>Bottleneck detection.</b> See exactly where work queues, stalls or gets re-touched.</span></li>
          <li>{chk}<span><b>Idle vs. active time.</b> Separate real processing from waiting on someone else.</span></li>
          <li>{chk}<span><b>SLA analytics.</b> Watch breach risk build across tasks and shifts, not after the fact.</span></li>
        </ul>
        <div className="hero-cta-row" style={{marginBottom: '0'}}>
          <a className="btn-primary" href="/register">Start free</a>
          <a className="btn-ghost" href="/book-demo">Get a demo for ops teams →</a>
        </div>
      </div>
      <div className="frame">
        <div className="frame-h">{dots}WORKFLOW HEALTH — TODAY, 2:10 PM</div>
        <div className="applist" style={{padding: '16px 18px'}}>
          <span className="ap"><i style={{background: '#34A851'}}></i>Intake &amp; triage<span className="bar"><b style={{width: '88%', background: '#34A851'}}></b></span><span className="pc">on</span></span>
          <span className="ap"><i style={{background: '#2DBDB8'}}></i>Verification<span className="bar"><b style={{width: '74%', background: '#2DBDB8'}}></b></span><span className="pc">on</span></span>
          <span className="ap"><i style={{background: '#F2673A'}}></i>Approval queue<span className="bar"><b style={{width: '31%', background: '#F2673A'}}></b></span><span className="pc">slow</span></span>
          <span className="ap"><i style={{background: '#469FD8'}}></i>Fulfilment<span className="bar"><b style={{width: '69%', background: '#469FD8'}}></b></span><span className="pc">on</span></span>
        </div>
        <div className="sumcard" style={{borderTop: '1px solid var(--line-soft)'}}>
          <div className="sc-t">FLAG — APPROVAL QUEUE</div>
          <p>Wait time is up <b>2.4x</b> since Monday. Two approvers are on the same shift. Moving one to the morning block clears <b>41 items</b> before the SLA window closes.</p>
        </div>
      </div>
    </div>
  </div>
</header>

<section className="sect" style={{paddingTop: '32px'}}>
  <div className="container">
    <h2 className="sect-h2">Throughput you can<br /><span className="dim">actually explain</span></h2>
    <p className="sect-sub">Real-time process visibility built from work itself. No screenshots, no keystrokes, patterns not people.</p>

    <div className="frow">
      <div className="fr-copy">
        <span className="fl" style={{color: '#F2673A'}}>BOTTLENECK DETECTION</span>
        <h3>The step that is holding everything up</h3>
        <p className="fd">TeamGrid highlights where work slows or queues across your process, then tells you what changed. Most ops leaders find their first real bottleneck in week one.</p>
        <ul className="fpts">
          <li><span className="sarr">→</span>Queue build-up flagged as it forms</li>
          <li><span className="sarr">→</span>Rework and re-touch loops surfaced by pattern</li>
          <li><span className="sarr">→</span>Compare shift to shift, site to site</li>
        </ul>
      </div>
      <div className="frame">
        <div className="frame-h">{dots}STAGE WAIT TIME — 7-DAY TREND</div>
        <div className="applist" style={{padding: '16px 18px'}}>
          <span className="ap">Intake<span className="bar"><b style={{width: '18%', background: '#34A851'}}></b></span><span className="pc">12m</span></span>
          <span className="ap">Verification<span className="bar"><b style={{width: '34%', background: '#2DBDB8'}}></b></span><span className="pc">48m</span></span>
          <span className="ap">Approval<span className="bar"><b style={{width: '92%', background: '#E0483D'}}></b></span><span className="pc">6.1h</span></span>
          <span className="ap">Dispatch<span className="bar"><b style={{width: '26%', background: '#469FD8'}}></b></span><span className="pc">31m</span></span>
        </div>
      </div>
    </div>

    <div className="frow rev">
      <div className="frame">
        <div className="frame-h">{dots}WHERE THE SHIFT WENT</div>
        <div className="anlist">
          <div className="ancard"><span className="aico" style={{background: '#e6f7f5'}}>⚙</span><span><span className="at" style={{color: '#007A6E'}}>PROCESSING</span><span className="am"><b>5.8h</b> of hands-on work across 214 cases</span></span></div>
          <div className="ancard"><span className="aico" style={{background: '#fff1ec'}}>⏳</span><span><span className="at" style={{color: '#b45318'}}>WAITING</span><span className="am"><b>1.9h</b> blocked on approvals and missing inputs</span></span></div>
          <div className="ancard"><span className="aico" style={{background: '#eef4fd'}}>↻</span><span><span className="at" style={{color: '#366EB6'}}>REWORK</span><span className="am"><b>36 cases</b> touched more than twice — one form field is the cause</span></span></div>
        </div>
      </div>
      <div className="fr-copy">
        <span className="fl" style={{color: '#007A6E'}}>IDLE VS. PRODUCTIVE TIME</span>
        <h3>Waiting is not the same as working</h3>
        <p className="fd">TeamGrid separates active processing from waiting and idle periods, so capacity conversations start from evidence instead of gut feel. That split is usually where the error rate hides too.</p>
        <ul className="fpts">
          <li><span className="sarr">→</span>Active vs. blocked time, per stage and per shift</li>
          <li><span className="sarr">→</span>Rework loops traced back to their trigger</li>
          <li><span className="sarr">→</span>Capacity planning from measured load, not headcount</li>
        </ul>
      </div>
    </div>

    <div className="frow">
      <div className="fr-copy">
        <span className="fl" style={{color: '#469FD8'}}>SLA TRACKING</span>
        <h3>Breach risk, hours before the breach</h3>
        <p className="fd">SLA-driven work needs a warning, not a post-mortem. TeamGrid tracks operational focus against the clock and raises the flag while the day can still be saved.</p>
        <ul className="fpts">
          <li><span className="sarr">→</span>Live SLA position by queue and client</li>
          <li><span className="sarr">→</span>Early warnings routed to the shift lead</li>
          <li><span className="sarr">→</span>Automatic write-up of what caused each miss</li>
        </ul>
      </div>
      <div className="frame">
        <div className="frame-h">{dots}SLA POSITION — LIVE</div>
        <div className="applist" style={{padding: '16px 18px'}}>
          <span className="ap">Tier 1 tickets<span className="bar"><b style={{width: '99%', background: '#34A851'}}></b></span><span className="pc">99.4%</span></span>
          <span className="ap">Claims review<span className="bar"><b style={{width: '97%', background: '#2DBDB8'}}></b></span><span className="pc">97.1%</span></span>
          <span className="ap">Onboarding<span className="bar"><b style={{width: '89%', background: '#F2A73A'}}></b></span><span className="pc">89.0%</span></span>
          <span className="ap">Escalations<span className="bar"><b style={{width: '100%', background: '#34A851'}}></b></span><span className="pc">100%</span></span>
        </div>
      </div>
    </div>
  </div>
</section>

<section className="sect" style={{paddingTop: '8px'}}>
  <div className="container">
    <div className="roi-cards" style={{marginTop: '0'}}>
      <div className="roi-card"><div className="rl">THROUGHPUT</div><div className="rv">+40%</div><p>Faster throughput once queue bottlenecks are named and cleared.</p></div>
      <div className="roi-card"><div className="rl">COST</div><div className="rv">−35%</div><p>Operating cost reduction from work routed to real capacity.</p></div>
      <div className="roi-card"><div className="rl">SLA</div><div className="rv">99.9%</div><p>SLA compliance held with early warnings instead of post-mortems.</p></div>
      <div className="roi-card"><div className="rl">ERRORS</div><div className="rv">−50%</div><p>Fewer errors when rework loops are traced back to their source.</p></div>
    </div>
    <p className="roi-note">*Typical results reported by TeamGrid operations workspaces.</p>
  </div>
</section>

<div className="container"><div className="workswith"><span className="wl">WORKS BEST WITH</span><a href="/pattern-intelligence">Pattern Intelligence →</a><a href="/monitoring">Workforce Analytics →</a></div></div>

<section className="sect" style={{padding: '8px 0 24px'}}>
  <div className="container">
    <h2 className="sect-h2" style={{fontSize: '28px', lineHeight: '36px'}}>Common questions</h2>
    <div className="faq">
      <details><summary>How does TeamGrid optimize operational workflows?</summary><p>It reads the shape of work across your process, finds where tasks queue or get repeated, and reports it in plain language. You get the bottleneck and the likely cause, not a wall of raw metrics.</p></details>
      <details><summary>Does it fit the tools we already run on?</summary><p>Yes. TeamGrid connects with 100+ operational tools — project systems, communication platforms and monitoring stacks — so the whole operation reads as one view.</p></details>
      <details><summary>How quickly do we see a difference?</summary><p>Most teams see measurable improvement within the first two weeks, with efficiency gains of 20-30% in the first month. Setup is one lightweight agent, not an IT project.</p></details>
      <details><summary>Does this work for distributed ops teams?</summary><p>Yes. Visibility, reporting and coordination work across time zones and locations, and every team is compared to its own baseline rather than to another site.</p></details>
    </div>
  </div>
</section>

<section className="final" style={{paddingTop: '48px'}}>
  <div className="container-wide">
    <div className="final-band" style={{paddingBottom: '88px'}}>
      <h2>Run the operation<br />on evidence</h2>
      <a className="btn-white" href="/register">Get started FREE</a>
    </div>
  </div>
</section>
    </>
  );
}
